import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Tags, DollarSign, AlertTriangle, Edit, Calendar, User, MapPin, Camera, Eye, X, RotateCcw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useInventory } from '../context/InventoryContext';
import RDWasteForm from '../components/rd/RDWasteForm';
import TransferFromWasteForm from '../components/locations/TransferFromWasteForm';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';

const RDWasteDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getCategoryName } = useInventory();
  const [showEditForm, setShowEditForm] = useState(false);
  const [showTransferBackForm, setShowTransferBackForm] = useState(false);
  const [wasteItem, setWasteItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  const isNewItem = id === 'new';

  useEffect(() => {
    if (!isNewItem && id) {
      loadWasteItem();
    } else {
      setLoading(false);
    }
  }, [id, isNewItem]);

  const loadWasteItem = async () => {
    try {
      const { data, error } = await supabase
        .from('rd_waste_items')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setWasteItem(data);
    } catch (error) {
      console.error('Error loading waste item:', error);
      setWasteItem(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!isNewItem && !wasteItem) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Waste Item Not Found</h2>
        <p className="text-gray-500 mb-6">The waste item you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/rd-waste')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to R&D Waste Management
        </Button>
      </div>
    );
  }

  if (isNewItem || showEditForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/rd-waste')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNewItem ? 'Add New Waste Item' : `Edit Waste Item: ${wasteItem?.item_id}`}
          </h1>
        </div>
        
        <RDWasteForm 
          item={wasteItem} 
          isEdit={!isNewItem} 
        />
      </div>
    );
  }

  const getCategoryBadgeVariant = (category: string) => {
    switch (category) {
      case 'broken': return 'danger';
      case 'discontinued': return 'warning';
      case 'abandoned': return 'secondary';
      case 'obsolete': return 'primary';
      case 'hazardous': return 'danger';
      case 'chemical': return 'warning';
      case 'biological': return 'warning';
      case 'electronic': return 'primary';
      case 'mechanical': return 'primary';
      case 'non_hazardous': return 'success';
      case 'other': return 'secondary';
      default: return 'secondary';
    }
  };

  const getDisposalBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'secondary';
      default: return 'secondary';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'broken': return 'Broken/Damaged';
      case 'discontinued': return 'Discontinued';
      case 'abandoned': return 'Abandoned';
      case 'obsolete': return 'Obsolete';
      case 'chemical': return 'Chemical';
      case 'biological': return 'Biological';
      case 'electronic': return 'Electronic';
      case 'mechanical': return 'Mechanical';
      case 'hazardous': return 'Hazardous';
      case 'non_hazardous': return 'Non-Hazardous';
      case 'other': return 'Other';
      default: return category || 'Other';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/rd-waste')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/rd-waste" className="text-sm text-gray-500 hover:text-gray-700">
                  R&D Waste Management
                </Link>
              </li>
              <li>
                <span className="text-sm text-gray-600">/</span>
              </li>
              <li>
                <span className="text-sm text-gray-900">{wasteItem?.item_id}</span>
              </li>
            </ol>
          </nav>
        </div>
        <div className="flex gap-2">
          {(wasteItem?.stock_quantity || 0) > 0 && (
            <Button
              variant="success"
              onClick={() => setShowTransferBackForm(true)}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Transfer Back
            </Button>
          )}
          <Button onClick={() => setShowEditForm(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            {/* Image Gallery */}
            {wasteItem?.images && wasteItem.images.length > 0 && (
              <div className="mb-6">
                <div className="aspect-w-16 aspect-h-9 mb-4">
                  <img
                    src={wasteItem.images[0]}
                    alt={wasteItem.original_project_name}
                    className="w-full h-64 object-cover bg-gray-50 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => {
                      setSelectedImage(wasteItem.images![0]);
                      setShowImageModal(true);
                    }}
                  />
                </div>
                
                {wasteItem.images.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {wasteItem.images.slice(1, 5).map((image, index) => (
                      <div
                        key={index}
                        className="aspect-square bg-gray-100 rounded overflow-hidden cursor-pointer hover:opacity-75 transition-opacity"
                        onClick={() => {
                          setSelectedImage(image);
                          setShowImageModal(true);
                        }}
                      >
                        <img
                          src={image}
                          alt={`${wasteItem.original_project_name} ${index + 2}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                    {wasteItem.images.length > 5 && (
                      <div className="aspect-square bg-gray-100 rounded flex items-center justify-center text-gray-500 text-xs font-medium">
                        +{wasteItem.images.length - 5} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">{wasteItem?.original_project_name}</h1>
                <div className="flex gap-2">
                  <Badge variant={getCategoryBadgeVariant(wasteItem?.category || '')}>
                    {getCategoryLabel(wasteItem?.category || '')}
                  </Badge>
                  <Badge variant={getDisposalBadgeVariant(wasteItem?.disposal_status || '')}>
                    {(wasteItem?.disposal_status || 'pending').replace('_', ' ')}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {getCategoryName(wasteItem?.category_id || '')}
                </Badge>
                <span className="text-sm text-gray-500">ID: {wasteItem?.item_id}</span>
                {wasteItem?.images && wasteItem.images.length > 0 && (
                  <Badge variant="primary">
                    <Camera className="h-3 w-3 mr-1" />
                    {wasteItem.images.length} photos
                  </Badge>
                )}
              </div>

              {wasteItem?.description && (
                <p className="text-gray-600">{wasteItem.description}</p>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2 text-red-600 mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-sm">Original Cost</span>
                  </div>
                  <span className="text-2xl font-bold text-red-900">
                    ${(wasteItem?.original_cost || 0).toLocaleString()}
                  </span>
                </div>

                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2 text-green-600 mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-sm">Salvage Value</span>
                  </div>
                  <span className="text-2xl font-bold text-green-900">
                    ${(wasteItem?.salvage_value || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              {wasteItem?.reason && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Reason for Waste Classification</h4>
                  <p className="text-sm text-gray-600">{wasteItem.reason}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Item Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-gray-200">
                <div className="py-3 grid grid-cols-3 gap-4 bg-blue-50">
                  <dt className="text-sm font-medium text-gray-700">Quantity</dt>
                  <dd className="text-lg font-bold text-blue-900 col-span-2">
                    {wasteItem?.stock_quantity || 0} units
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Project Code</dt>
                  <dd className="text-sm text-blue-600 font-medium col-span-2">
                    {wasteItem?.project_code || 'N/A'}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Researcher</dt>
                  <dd className="text-sm text-gray-900 col-span-2">{wasteItem?.researcher || 'N/A'}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Project Team</dt>
                  <dd className="text-sm text-gray-900 col-span-2">{wasteItem?.project_team || 'N/A'}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Storage Location</dt>
                  <dd className="text-sm text-gray-900 col-span-2">{wasteItem?.storage_location || 'N/A'}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Date Added</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {wasteItem?.date_added ? new Date(wasteItem.date_added).toLocaleDateString() : 'N/A'}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Disposal Method</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {wasteItem?.disposal_method ? wasteItem.disposal_method.replace('_', ' ') : 'Not specified'}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Environmental Impact</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {wasteItem?.environmental_impact ? (
                      <Badge variant={wasteItem.environmental_impact === 'low' ? 'success' : 'warning'}>
                        {wasteItem.environmental_impact}
                      </Badge>
                    ) : (
                      <span className="text-gray-500">Not specified</span>
                    )}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Financial Impact</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">Original Investment:</span>
                  <span className="text-sm font-bold text-red-600">
                    ${(wasteItem?.original_cost || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">Salvage Value:</span>
                  <span className="text-sm font-bold text-green-600">
                    ${(wasteItem?.salvage_value || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">Disposal Cost:</span>
                  <span className="text-sm font-bold text-gray-600">
                    ${(wasteItem?.disposal_cost || 0).toLocaleString()}
                  </span>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-900">Net Loss:</span>
                  <span className="text-lg font-bold text-red-600">
                    ${((wasteItem?.original_cost || 0) - (wasteItem?.salvage_value || 0) + (wasteItem?.disposal_cost || 0)).toLocaleString()}
                  </span>
                </div>
                
                {/* Recovery Percentage */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Value Recovery</span>
                    <span>
                      {Math.round(((wasteItem?.salvage_value || 0) / (wasteItem?.original_cost || 1)) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ 
                        width: `${Math.min(((wasteItem?.salvage_value || 0) / (wasteItem?.original_cost || 1)) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {wasteItem?.disposal_status === 'pending' && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Disposal Pending</p>
                    <p className="text-sm">
                      This item is awaiting disposal processing. Please coordinate with the waste management team.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Transfer Back Form Modal */}
      {showTransferBackForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <TransferFromWasteForm
              wasteItem={wasteItem}
              onTransferComplete={() => {
                setShowTransferBackForm(false);
                loadWasteItem();
              }}
              onCancel={() => setShowTransferBackForm(false)}
            />
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <Button
              variant="secondary"
              className="absolute top-4 right-4 z-10"
              onClick={() => {
                setShowImageModal(false);
                setSelectedImage(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
            <img
              src={selectedImage}
              alt="Waste item detail"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            
            {/* Image Navigation */}
            {wasteItem?.images && wasteItem.images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {wasteItem.images.map((image, index) => (
                  <button
                    key={index}
                    className={`w-3 h-3 rounded-full ${
                      image === selectedImage ? 'bg-white' : 'bg-white bg-opacity-50'
                    }`}
                    onClick={() => setSelectedImage(image)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RDWasteDetailPage;